import type { i18n } from '@/i18n'
import router from '@/router'
import type { UserStore } from 'env'
import type { useToast } from 'vue-toastification'

import { RealTimeEvents } from './real-time-events'

type RewardToastSystemInitializeParams = {
  toast: ReturnType<typeof useToast>
  userStore: UserStore
  i18n: typeof i18n.global
}

// Constants
export const PENDING_REFERRAL_BOX_CODES_KEY = 'pending_referral_box_codes'
const MAX_PROCESSED_KEYS = 200

// Type definitions for reward event payloads
type RewardEventToastMetadata = {
  message_authenticated?: string
  message_unauthenticated?: string
  cta_url?: string
}

type RewardEventCredit = {
  amount: number
  reservation_id?: string
  message_id?: string
  metadata?: {
    toast?: RewardEventToastMetadata
  }
  campaign_metadata?: {
    toast?: RewardEventToastMetadata
  }
}

type RewardEventXP = {
  points: number
  reservation_id?: string
  message_id?: string
}

type RewardEventEntitlement = {
  type: string
  units?: number
  plan_label?: string
  reservation_id?: string
  message_id?: string
  metadata?: {
    toast?: RewardEventToastMetadata
  }
  campaign_metadata?: {
    toast?: RewardEventToastMetadata
  }
}

type RewardEventReferralBox = {
  referral_box_code: string
  reservation_id?: string
  message_id?: string
}

type RewardEventPayload = {
  credits?: RewardEventCredit[]
  xp?: RewardEventXP[]
  entitlements?: RewardEventEntitlement[]
  referral_boxes?: RewardEventReferralBox[]
}

// Union type for items that can be deduplicated
type DedupableRewardEventItem =
  | RewardEventCredit
  | RewardEventXP
  | RewardEventEntitlement
  | RewardEventReferralBox

/**
 * Generic toast notification system for rewards.
 * Handles both simple credit amounts and complex reward payloads.
 */
export class RewardToastSystem {
  // Track active subscriptions to prevent duplicates
  private static activeSubscriptions = new Set<string>()
  private static processedEventKeys = new Set<string>()
  private static processedEventQueue: string[] = []
  private static initialized = false
  private static warnedNotInitialized = false

  private static openCta(ctaUrl?: string) {
    if (!ctaUrl) {
      return
    }
    try {
      if (ctaUrl.startsWith('/')) {
        void router.push({ path: ctaUrl })
      } else {
        window.location.href = ctaUrl
      }
    } catch (error: unknown) {
      console.error('Failed to open CTA URL', error)
    }
  }

  private static ensureInitialized(): boolean {
    if (RewardToastSystem.initialized) {
      return true
    }
    if (!RewardToastSystem.warnedNotInitialized) {
      console.warn('[RewardToastSystem] initialize() was not called before use')
      RewardToastSystem.warnedNotInitialized = true
    }
    return false
  }

  private static addProcessedKey(key: string) {
    RewardToastSystem.processedEventKeys.add(key)
    RewardToastSystem.processedEventQueue.push(key)
    if (RewardToastSystem.processedEventQueue.length > MAX_PROCESSED_KEYS) {
      const oldest = RewardToastSystem.processedEventQueue.shift()
      if (oldest) {
        RewardToastSystem.processedEventKeys.delete(oldest)
      }
    }
  }

  private static registerDedupKey(
    type: string,
    item: DedupableRewardEventItem
  ): boolean {
    const key = `${type}|${item?.reservation_id ?? 'na'}|${
      item?.message_id ?? 'na'
    }`

    if (RewardToastSystem.processedEventKeys.has(key)) {
      return false
    }

    RewardToastSystem.addProcessedKey(key)
    return true
  }

  public static routeToReferralBox(code: string) {
    try {
      const current = router.currentRoute.value
      if (current.name === 'referral-box' && current.query?.code === code) {
        // Already on the same code route; no-op
        return
      }
      if (current.name === 'referral-box') {
        void router.replace({ name: 'referral-box', query: { code } })
      } else {
        void router.push({ name: 'referral-box', query: { code } })
      }
    } catch (error: unknown) {
      console.error('Failed to route to referral box', error)
    }
  }

  private static toast: RewardToastSystemInitializeParams['toast']
  private static userStore: RewardToastSystemInitializeParams['userStore']
  private static i18n: RewardToastSystemInitializeParams['i18n']

  public static initialize({
    toast,
    userStore,
    i18n,
  }: RewardToastSystemInitializeParams) {
    RewardToastSystem.toast = toast
    RewardToastSystem.userStore = userStore
    RewardToastSystem.i18n = i18n
    RewardToastSystem.initialized = true
  }

  /**
   * Shows a credit reward notification.
   * @param {number} creditsAmount - Credit amount in pence or pounds
   * @param {boolean} isPence - Whether the amount is in pence (default: false)
   */
  static async showCreditToast(
    creditsAmount: number,
    isPence: boolean = false,
    messageOverride?: string,
    ctaUrl?: string
  ) {
    const amount = isPence ? creditsAmount / 100 : creditsAmount
    const formatted = RewardToastSystem.i18n.n(amount, 'currency')
    const defaultMessage = RewardToastSystem.userStore.isLoggedIn
      ? `You've received ${formatted} credit!`
      : `You've received ${formatted}! Sign in to view it.`
    const message = messageOverride ?? defaultMessage

    RewardToastSystem.toast.success(message, {
      timeout: false,
      closeOnClick: Boolean(ctaUrl),
      onClick: () => RewardToastSystem.openCta(ctaUrl),
    })
  }

  /**
   * Shows an XP reward notification.
   * @param {number} xpAmount - XP points earned
   */
  static async showXPToast(xpAmount: number) {
    const message = `You've earned ${xpAmount} XP points!`
    RewardToastSystem.toast.success(message, { timeout: false })
  }

  /**
   * Shows an entitlement reward notification.
   * @param {string} type - Entitlement type
   * @param {number} units - Number of units (for instant_win, etc.)
   */
  static async showEntitlementToast(
    type: string,
    units: number = 1,
    label?: string,
    ctaUrl?: string
  ) {
    let message = ''

    switch (type) {
      case 'instant_win':
        message = `You got ${units} instant prize ${units === 1 ? 'entry' : 'entries'}!`
        break
      case 'plan':
        message = label
          ? `You've received a ${label} plan!`
          : 'You received a discounted plan!'
        break
      case 'coupon':
        message = 'You received a discount coupon!'
        break
      default:
        message = `You received a ${type} reward!`
    }

    if (message) {
      RewardToastSystem.toast.success(message, {
        timeout: false,
        closeOnClick: Boolean(ctaUrl),
        onClick: () => RewardToastSystem.openCta(ctaUrl),
      })
    }
  }

  /**
   * Handles rewards claimed broadcast events.
   * @param {RewardEventPayload} event - The broadcast event payload
   */
  static async handleRewardsBroadcast(event: RewardEventPayload) {
    if (!RewardToastSystem.ensureInitialized()) {
      console.warn(
        '[RewardToastSystem] Dropped reward event due to uninitialized system:',
        event
      )
      return
    }

    await RewardToastSystem.processCreditRewards(event?.credits)
    await RewardToastSystem.processXpRewards(event?.xp)
    await RewardToastSystem.processEntitlementRewards(event?.entitlements)
    await RewardToastSystem.processReferralBoxes(event?.referral_boxes)
  }

  private static async processCreditRewards(
    credits?: RewardEventCredit[]
  ): Promise<void> {
    if (!Array.isArray(credits) || credits.length === 0) {
      return
    }

    let totalNewCredits = 0
    let anyPerItemMessage = false

    for (const credit of credits) {
      if (!RewardToastSystem.registerDedupKey('credit', credit)) {
        continue
      }

      totalNewCredits += credit.amount ?? 0
      const toastMeta =
        credit.metadata?.toast || credit.campaign_metadata?.toast
      const message = toastMeta
        ? RewardToastSystem.userStore.isLoggedIn
          ? (toastMeta.message_authenticated ?? null)
          : (toastMeta.message_unauthenticated ?? null)
        : null

      if (message) {
        anyPerItemMessage = true
        await RewardToastSystem.showCreditToast(
          credit.amount,
          true,
          message,
          toastMeta?.cta_url
        )
      }
    }

    if (!anyPerItemMessage && totalNewCredits > 0) {
      await RewardToastSystem.showCreditToast(totalNewCredits, true)
    }

    if (totalNewCredits > 0 && RewardToastSystem.userStore.isLoggedIn) {
      void RewardToastSystem.userStore
        .fetchCreditBalance()
        .catch((error: unknown) => {
          console.error('Failed to refresh credit balance', error)
        })
    }
  }

  private static async processXpRewards(
    rewards?: RewardEventXP[]
  ): Promise<void> {
    if (!Array.isArray(rewards) || rewards.length === 0) {
      return
    }

    let totalNewXp = 0

    for (const xp of rewards) {
      if (!RewardToastSystem.registerDedupKey('xp', xp)) {
        continue
      }

      totalNewXp += xp.points ?? 0
    }

    if (totalNewXp > 0) {
      await RewardToastSystem.showXPToast(totalNewXp)
      if (
        RewardToastSystem.userStore.isLoggedIn &&
        RewardToastSystem.userStore.user?.user_progression
      ) {
        RewardToastSystem.userStore.user.user_progression.xp_earned +=
          totalNewXp
      }
    }
  }

  private static async processEntitlementRewards(
    entitlements?: RewardEventEntitlement[]
  ): Promise<void> {
    if (!Array.isArray(entitlements) || entitlements.length === 0) {
      return
    }

    for (const entitlement of entitlements) {
      if (!RewardToastSystem.registerDedupKey('entitlement', entitlement)) {
        continue
      }

      const toastMeta =
        entitlement.campaign_metadata?.toast || entitlement.metadata?.toast
      const label = toastMeta
        ? RewardToastSystem.userStore.isLoggedIn
          ? (toastMeta.message_authenticated ?? undefined)
          : (toastMeta.message_unauthenticated ?? undefined)
        : undefined

      await RewardToastSystem.showEntitlementToast(
        entitlement.type,
        entitlement.units ?? 1,
        label,
        toastMeta?.cta_url
      )
    }
  }

  private static async processReferralBoxes(
    referralBoxes?: RewardEventReferralBox[]
  ): Promise<void> {
    if (!Array.isArray(referralBoxes) || referralBoxes.length === 0) {
      return
    }

    for (const referralBox of referralBoxes) {
      if (!RewardToastSystem.registerDedupKey('referral_box', referralBox)) {
        continue
      }

      const code = referralBox.referral_box_code

      if (!code) {
        continue
      }

      if (RewardToastSystem.userStore.isLoggedIn) {
        RewardToastSystem.routeToReferralBox(code)
        continue
      }

      try {
        const rawExisting =
          localStorage.getItem(PENDING_REFERRAL_BOX_CODES_KEY) || '[]'
        const parsedExisting = JSON.parse(rawExisting) as unknown
        const existingCodes = Array.isArray(parsedExisting)
          ? parsedExisting.filter(
              (item): item is string => typeof item === 'string'
            )
          : []

        if (!existingCodes.includes(code)) {
          existingCodes.push(code)
          localStorage.setItem(
            PENDING_REFERRAL_BOX_CODES_KEY,
            JSON.stringify(existingCodes)
          )
        }
      } catch (error: unknown) {
        console.error('Failed to persist referral box code', error)
      }
    }
  }

  /**
   * Sets up rewards subscription for a user.
   * @param {number} userId - The user ID
   * @returns {boolean} - True if subscription was created, false if already exists
   */
  static async setupRewardsSubscription(userId: number): Promise<boolean> {
    const subscriptionKey = `customer.${userId}`

    // Check if already subscribed
    if (this.activeSubscriptions.has(subscriptionKey)) {
      console.log(`Already subscribed to rewards for customer ${userId}`)
      return false
    }

    try {
      const customerChannelName = RealTimeEvents.getChannel('Customer', userId)

      RealTimeEvents.subscribe(
        customerChannelName,
        RealTimeEvents.Events.RewardsClaimed,
        true, // public channel
        (event: RewardEventPayload) => {
          void this.handleRewardsBroadcast(event)
        }
      )

      this.activeSubscriptions.add(subscriptionKey)
      console.log(`Subscribed to rewards claimed events for customer ${userId}`)
      return true
    } catch (error: unknown) {
      console.error('Failed to setup rewards subscription:', error)
      return false
    }
  }

  /**
   * Cleans up rewards subscription for a user.
   * @param {number} userId - The user ID
   * @returns {boolean} - True if subscription was removed, false if not found
   */
  static async cleanupRewardsSubscription(userId: number): Promise<boolean> {
    const subscriptionKey = `customer.${userId}`

    if (!this.activeSubscriptions.has(subscriptionKey)) {
      return false
    }

    try {
      const customerChannelName = RealTimeEvents.getChannel('Customer', userId)

      RealTimeEvents.unsubscribe(
        customerChannelName,
        RealTimeEvents.Events.RewardsClaimed
      )
      this.activeSubscriptions.delete(subscriptionKey)

      console.log(
        `Unsubscribed from rewards claimed events for customer ${userId}`
      )
      return true
    } catch (error: unknown) {
      console.error('Failed to cleanup rewards subscription:', error)
      return false
    }
  }

  /**
   * Cleans up all active subscriptions.
   * Should be called on logout or app cleanup.
   */
  static async cleanupAllSubscriptions(): Promise<void> {
    for (const subscriptionKey of this.activeSubscriptions) {
      try {
        const userId = subscriptionKey.replace('customer.', '')
        const customerChannelName = RealTimeEvents.getChannel(
          'Customer',
          parseInt(userId)
        )

        RealTimeEvents.unsubscribe(
          customerChannelName,
          RealTimeEvents.Events.RewardsClaimed
        )
      } catch (error: unknown) {
        console.error('Error cleaning up subscription:', subscriptionKey, error)
      }
    }

    this.activeSubscriptions.clear()
    this.processedEventKeys.clear()
    this.processedEventQueue.length = 0
    console.log('Cleaned up all rewards subscriptions and processed event keys')
  }
}
